import { Flex, Form } from 'antd'
import { FunctionComponent } from 'react'
import styled from 'styled-components'
import { useAddCollaborationRecordScoringMutation } from '@/api/workspace-api/directory-api'
import { BasicModal } from '@/components/common/antd/basic-modal'
import ScoringForm from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history/scoring-form'
import useNotification from '@/hooks/antd/use-notification'
import { useSelector } from '@/hooks/use-selector'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  CollaborationRecordScoringType,
  DraftScoring,
} from '@/types/schema/directory/collaboration-record'
import { ampli } from '@/utils/ampli'
import { exhaustiveCheck } from '@/utils/exhaustive-check'
import logger from '@/utils/logger'
import { toVoidFunction } from '@/utils/to-void-function'

interface AddScoreModalProps {
  open: boolean
  onClose: VoidFunction
  kolId: string
  kolName: string
  collaborationRecordId: number
  collaborationRecordName: string
  collaborationRecordScoringType: CollaborationRecordScoringType
}

const AddScoringModal: FunctionComponent<AddScoreModalProps> = ({
  open,
  onClose,
  kolId,
  kolName,
  collaborationRecordId,
  collaborationRecordName,
  collaborationRecordScoringType,
}) => {
  const { formatMessage } = useIntl()
  const [form] = Form.useForm<DraftScoring>()
  const notification = useNotification()

  const {
    data: { at: from },
  } = useSelector((state) => state.kolCooperationDetailDrawer)

  const [addCollaborationRecordScoring, { isLoading }] =
    useAddCollaborationRecordScoringMutation()

  const handleFormFinish = async (values: DraftScoring): Promise<void> => {
    try {
      const { collaboration_record_scoring_id } =
        await addCollaborationRecordScoring({
          kolId,
          collaboration_record_id: collaborationRecordId,
          collaboration_record_scoring_type: collaborationRecordScoringType,
          evaluation_content: values.evaluation_content,
          evaluation_score: values.evaluation_score * 2,
        }).unwrap()

      const sharedEventProperties = {
        kolId,
        kolName,
        action: 'Add',
        feedbackId: collaboration_record_scoring_id,
        from,
        rating: values.evaluation_score,
        fieldValue: values.evaluation_content,
      } as const

      switch (collaborationRecordScoringType) {
        case CollaborationRecordScoringType.CampaignKol:
          ampli.configureFeedbackForCampaign({
            ...sharedEventProperties,
            campaignId: collaborationRecordId,
            campaignName: collaborationRecordName,
          })
          break
        case CollaborationRecordScoringType.ReportKol:
          ampli.configureFeedbackForDeepReport({
            ...sharedEventProperties,
            reportId: collaborationRecordId,
            reportName: collaborationRecordName,
          })
          break
        case CollaborationRecordScoringType.Customized:
          ampli.configureFeedbackForCustomCollaboration({
            ...sharedEventProperties,
            collaborationId: collaborationRecordId,
            collaborationName: collaborationRecordName,
          })
          break
        default:
          exhaustiveCheck(collaborationRecordScoringType)
      }

      onClose()
    } catch (error) {
      logger.error(error)

      notification.error({
        message: formatMessage({
          id: 'general:operation_failure',
        }),
        placement: 'bottomRight',
      })
    }
  }

  return (
    <BasicModal
      centered
      destroyOnClose
      footer={(_, { OkBtn }) => {
        return (
          <Flex align='center' justify='end'>
            <OkBtn />
          </Flex>
        )
      }}
      okButtonProps={{ type: 'primary', loading: isLoading }}
      okText={formatMessage({ id: 'general:submit_btn' })}
      open={open}
      title={formatMessage({
        id: 'irm:title_created_cooperation_memo',
      })}
      width={520}
      onCancel={onClose}
      onOk={form.submit}
    >
      <Hint>
        {formatMessage({
          id: 'kol_cooperation_detail_drawer:add_scoring_modal_hint',
        })}
      </Hint>
      <ScoringForm form={form} onFinish={toVoidFunction(handleFormFinish)} />
    </BasicModal>
  )
}

const Hint = styled.div`
  color: ${({ theme }): string => theme.colors.text.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  margin: 0 0 12px;
`

export default AddScoringModal
